/**
 * Money and price arithmetic.
 *
 * THE RULE: every cash quantity in AEGIS is a Decimal, never a JS number.
 *
 * Binary floating point cannot represent decimal prices exactly. A journal that
 * computes P&L in floats accumulates error and eventually disagrees with the
 * broker statement — at which point the user stops trusting the entire product.
 *
 * `ZN` makes this concrete: its tick value is $15.625, which has no exact
 * binary representation. Ten thousand ZN ticks summed as floats will not equal
 * the same sum computed exactly.
 *
 * The split, stated once so it is not re-litigated per module:
 *
 *   - CASH  (P&L, commissions, fees, balances, margin) -> ALWAYS Decimal.
 *   - PRICE (stored on a trade, compared to a level)   -> ALWAYS Decimal.
 *   - CHART/INDICATOR maths (EMA over 1M bars, pixel   -> number is fine.
 *     coordinates, signal thresholds)                     These are display
 *                                                          concerns and never
 *                                                          feed a cash figure.
 *
 * @see docs/10-backend-and-data-model.md — NUMERIC, never FLOAT
 */
import Decimal from 'decimal.js';

// 28 significant digits is far more than any futures price or cash figure
// needs, and ROUND_HALF_EVEN (banker's rounding) avoids the upward bias that
// ROUND_HALF_UP introduces when summing many rounded values.
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_EVEN });

export { Decimal };

/** A cash amount or an exact price. Always construct via {@link dec}. */
export type Money = Decimal;

/** Anything that can be turned into a Decimal without precision loss. */
export type DecimalLike = string | number | Decimal;

/**
 * Construct a Decimal.
 *
 * Passing a `number` is allowed because instrument specs and test fixtures are
 * written as literals, but note that the number itself has already been through
 * binary floating point. For values that arrive from a broker or a database,
 * pass the string.
 */
export function dec(v: DecimalLike): Decimal {
  return v instanceof Decimal ? v : new Decimal(v);
}

export const ZERO: Decimal = new Decimal(0);
export const ONE: Decimal = new Decimal(1);

/** Sum a list exactly. Empty list sums to zero, not NaN. */
export function sum(values: readonly DecimalLike[]): Decimal {
  return values.reduce<Decimal>((a, b) => a.plus(dec(b)), ZERO);
}

/** Round to cents for display. Never use the result for further arithmetic. */
export function toCents(v: DecimalLike): Decimal {
  return dec(v).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN);
}

/** Format as a USD string, e.g. `-$1,234.50`. */
export function formatUsd(v: DecimalLike, opts: { sign?: boolean } = {}): string {
  const d = dec(v);
  const neg = d.isNegative();
  const abs = d.abs().toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN);
  const [whole = '0', frac = '00'] = abs.toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = `$${grouped}.${frac}`;
  if (neg) return `-${body}`;
  return opts.sign ? `+${body}` : body;
}

/** True when two decimals are equal. Guards against `===` on Decimal objects. */
export function eq(a: DecimalLike, b: DecimalLike): boolean {
  return dec(a).eq(dec(b));
}

/** Serialise for JSON transport / DB write. Always a string — never a number. */
export function toJson(v: DecimalLike): string {
  return dec(v).toFixed();
}
